import React, { useState, useEffect, useRef } from 'react';
import { Model } from './ModelSelector';

interface ModelSelectorDialogProps {
  models: Model[];
  selectedModel: string;
  onSelect: (model: string) => void;
  onClose: () => void;
  position: { top: number; left: number; width: number };
}

// Helper to format price for display
const formatPrice = (price: number): string => {
  if (price === 0) return 'N/A';
  if (price >= 1) {
    return `$${price}/M`;
  } else {
    return `$${price.toFixed(2)}/M`;
  }
};

// Helper to capitalize provider names
const formatProviderName = (provider: string): string => {
  const providerNames: Record<string, string> = {
    'anthropic': 'Anthropic',
    'openai': 'OpenAI',
    'xai': 'xAI',
  };
  return providerNames[provider] || provider;
};

const RatingDots: React.FC<{ rating: number; max?: number }> = ({ rating, max = 5 }) => {
  return (
    <div className="flex gap-1">
      {Array.from({ length: max }).map((_, i) => (
        <div
          key={i}
          className={`w-2 h-2 rounded-full ${
            i < rating ? 'bg-slate-600' : 'bg-slate-200'
          }`}
        />
      ))}
    </div>
  );
};

export const ModelSelectorDialog: React.FC<ModelSelectorDialogProps> = ({
  models,
  selectedModel,
  onSelect,
  onClose,
  position,
}) => {
  const [hoveredModel, setHoveredModel] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dialogRef.current && !dialogRef.current.contains(event.target as Node)) {
        // Check if the click target is the model selector button or its children
        const target = event.target as HTMLElement;
        const isModelSelectorButton = target.closest('[data-model-selector-button="true"]');
        
        if (!isModelSelectorButton) {
          onClose();
        }
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  const displayModel = hoveredModel || selectedModel;
  const currentModelInfo = models.find(m => m.model === displayModel);

  // Group models by provider
  const groupedModels = models.reduce((acc, model) => {
    if (!acc[model.provider]) {
      acc[model.provider] = [];
    }
    acc[model.provider].push(model);
    return acc;
  }, {} as Record<string, Model[]>);

  return (
    <div
      ref={dialogRef}
      className="fixed z-50 bg-white border border-gray-200 rounded-lg shadow-lg"
      style={{
        bottom: `calc(100vh - ${position.top}px + 8px)`,
        left: `${position.left}px`,
        width: `${position.width}px`,
      }}
    >
      <div className="flex h-[400px]">
        {/* Left column - Model list */}
        <div className="w-2/5 border-r border-gray-200 p-4 overflow-y-auto">
          {Object.entries(groupedModels).map(([provider, providerModels]) => (
            <div key={provider} className="mb-4">
              <div className="text-xs text-gray-500 uppercase mb-2">
                {formatProviderName(provider)}
              </div>
              {providerModels.map((model) => (
                <div
                  key={model.model}
                  className={`px-3 py-2 rounded cursor-pointer transition-colors text-sm ${
                    selectedModel === model.model
                      ? 'bg-blue-50 text-blue-700 font-medium'
                      : 'hover:bg-gray-50'
                  }`}
                  onMouseEnter={() => setHoveredModel(model.model)}
                  onMouseLeave={() => setHoveredModel(null)}
                  onClick={() => onSelect(model.model)}
                >
                  {model.name}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Right column - Model details */}
        <div className="w-3/5 p-4">
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-2">
              {currentModelInfo?.name || 'Model'}
            </h3>
            <p className="text-sm text-gray-600 line-clamp-3 h-[3.75rem]">
              {currentModelInfo?.description || 'Select a model to see details'}
            </p>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Performance</span>
              <RatingDots rating={currentModelInfo?.performance || 0} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Speed</span>
              <RatingDots rating={currentModelInfo?.speed || 0} />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Reasoning</span>
              <RatingDots rating={currentModelInfo?.reasoning || 0} />
            </div>
          </div>

          <div className="border-t pt-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-gray-500">Input:</div>
                <div className="font-medium">{formatPrice(currentModelInfo?.inputPrice || 0)}</div>
              </div>
              <div>
                <div className="text-gray-500">Cache Read:</div>
                <div className="font-medium">{formatPrice(currentModelInfo?.cacheReadPrice || 0)}</div>
              </div>
              <div>
                <div className="text-gray-500">Output:</div>
                <div className="font-medium">{formatPrice(currentModelInfo?.outputPrice || 0)}</div>
              </div>
              <div>
                <div className="text-gray-500">Cache Write:</div>
                <div className="font-medium">{formatPrice(currentModelInfo?.cacheWritePrice || 0)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};